import { Section } from "@/types/article";
import {
  ChevronDown,
  ChevronRight,
  Edit2,
  Trash2,
  Check,
  X,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface SectionItemProps {
  section: Section;
  isExpanded: boolean;
  isEditing: boolean;
  editTitle: string;
  editingPointIndex: number | null;
  editingPointText: string;
  onToggleExpand: () => void;
  onEditStart: () => void;
  onEditCancel: () => void;
  onEditSave: (title: string) => void;
  onTitleChange: (title: string) => void;
  onDelete: () => void;
  onAddPoint: () => void;
  onEditPointStart: (pointIndex: number, currentText: string) => void;
  onEditPointCancel: () => void;
  onEditPointSave: (pointIndex: number, text: string) => void;
  onEditPointTextChange: (text: string) => void;
  onRemovePoint: (pointIndex: number) => void;
}

// Section item component to display each section with its points
export const SectionItem: React.FC<SectionItemProps> = ({
  section,
  isExpanded,
  isEditing,
  editTitle,
  editingPointIndex,
  editingPointText,
  onToggleExpand,
  onEditStart,
  onEditCancel,
  onEditSave,
  onTitleChange,
  onDelete,
  onAddPoint,
  onEditPointStart,
  onEditPointCancel,
  onEditPointSave,
  onEditPointTextChange,
  onRemovePoint,
}) => {
  return (
    <div className="border border-gray-200 rounded-md overflow-hidden mb-2">
      <div className="flex items-center p-3 bg-gray-50 border-b border-gray-200">
        <button
          onClick={onToggleExpand}
          className="mr-2 text-gray-500 hover:text-gray-700"
          aria-label={isExpanded ? "Collapse section" : "Expand section"}
        >
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>

        <div className="flex-shrink-0 w-8 h-6 bg-gray-200 rounded flex items-center justify-center mr-2">
          <span className="text-xs font-medium text-gray-600">H2</span>
        </div>

        {isEditing ? (
          <div className="flex-1 flex items-center">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => onTitleChange(e.target.value)}
              className="flex-1 px-2 py-1 border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <div className="flex ml-2">
              <button
                onClick={() => onEditSave(editTitle)}
                className="p-1 rounded-md hover:bg-green-100 text-green-600 mr-1"
                title="Save changes"
              >
                <Check size={16} />
              </button>
              <button
                onClick={onEditCancel}
                className="p-1 rounded-md hover:bg-red-100 text-red-600"
                title="Cancel"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 font-medium text-gray-800">
              {section.title}
            </div>

            <div className="flex space-x-1">
              <button
                onClick={onEditStart}
                className="p-1 rounded-md hover:bg-gray-200 text-gray-500 hover:text-blue-500"
                title="Edit section"
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={onDelete}
                className="p-1 rounded-md hover:bg-gray-200 text-gray-500 hover:text-red-500"
                title="Delete section"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      {isExpanded && (
        <div className="p-3 bg-white">
          {/* Points list */}
          {section.points && section.points.length > 0 ? (
            <div className="space-y-2 mb-3">
              {section.points.map((point, pointIndex) => (
                <div key={pointIndex} className="flex items-center gap-2">
                  {editingPointIndex === pointIndex ? (
                    <>
                      <input
                        type="text"
                        value={editingPointText}
                        onChange={(e) => onEditPointTextChange(e.target.value)}
                        className="flex-1 px-3 py-2 border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                      <div className="flex gap-1">
                        <button
                          onClick={() =>
                            onEditPointSave(pointIndex, editingPointText)
                          }
                          className="p-1 rounded-md hover:bg-green-100 text-green-600"
                          title="Save changes"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={onEditPointCancel}
                          className="p-1 rounded-md hover:bg-red-100 text-red-600"
                          title="Cancel"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded text-gray-700">
                        {point}
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => onEditPointStart(pointIndex, point)}
                          className="p-1 rounded-md hover:bg-gray-200 text-gray-500 hover:text-blue-500"
                          title="Edit point"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => onRemovePoint(pointIndex)}
                          className="p-1 rounded-md hover:bg-gray-200 text-gray-500 hover:text-red-500"
                          title="Remove point"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic text-sm mb-3">
              No points added yet. Add some points to outline this section.
            </p>
          )}

          {/* Add point button */}
          <Button
            onClick={onAddPoint}
            variant="outline"
            size="sm"
            className="w-full justify-center text-gray-600 hover:text-gray-800 border-dashed"
          >
            <Plus size={16} className="mr-1" />
            Add point
          </Button>
        </div>
      )}
    </div>
  );
};
